import { openDB } from 'idb';
import { toStoredMessage } from '~/composables/useMessageTombstone.js';

export const useMessagingDatabase = async () => {
    const dbName = 'TapLeaguesMessaging';
    const dbVersion = 4; // Incremented to add imageUploadQueue store

    // Helper to convert reactive/proxy objects to plain objects for IndexedDB
    const toPlain = (obj) => {
        return JSON.parse(JSON.stringify(obj));
    };

    // Helper to ensure timestamp is ISO string
    const ensureISOTimestamp = (date) => {
        if (!date) return null;
        if (typeof date === 'string') return date;
        if (date instanceof Date) return date.toISOString();
        return new Date(date).toISOString();
    };

    const db = await openDB(dbName, dbVersion, {
        upgrade(db, oldVersion) {
            // Conversations store
            if (!db.objectStoreNames.contains('conversations')) {
                const conversationStore = db.createObjectStore('conversations', {
                    keyPath: ['_userId', 'id']
                });
                conversationStore.createIndex('userId', '_userId');
                conversationStore.createIndex('userIdLastMessage', ['_userId', 'lastMessageAt']);
                conversationStore.createIndex('userIdSyncState', ['_userId', '_syncState']);
            }

            // Messages store
            if (!db.objectStoreNames.contains('messages')) {
                const messageStore = db.createObjectStore('messages', {
                    keyPath: ['_userId', 'id']
                });
                messageStore.createIndex('userId', '_userId');
                messageStore.createIndex('userIdConversation', ['_userId', 'conversationId']);
                messageStore.createIndex('userIdConversationCreated', ['_userId', 'conversationId', 'createdAt']);
                messageStore.createIndex('userIdSyncState', ['_userId', '_syncState']);
            }

            // Sync state store
            if (!db.objectStoreNames.contains('messageSyncState')) {
                db.createObjectStore('messageSyncState', { keyPath: 'userId' });
            }

            // Users cache store (added in v2)
            if (!db.objectStoreNames.contains('users')) {
                const userStore = db.createObjectStore('users', {
                    keyPath: 'id'
                });
                userStore.createIndex('id', 'id');
            }

            // Conversation reads store (added in v3)
            if (!db.objectStoreNames.contains('conversationReads')) {
                const readsStore = db.createObjectStore('conversationReads', {
                    keyPath: ['_userId', 'conversationId', 'userId']
                });
                readsStore.createIndex('userId_conversation', ['_userId', 'conversationId']);
            }

            // Image upload queue store (added in v4)
            if (!db.objectStoreNames.contains('imageUploadQueue')) {
                const queueStore = db.createObjectStore('imageUploadQueue', {
                    keyPath: ['_userId', 'id']
                });
                queueStore.createIndex('userId', '_userId');
                queueStore.createIndex('userIdStatus', ['_userId', 'status']);
                queueStore.createIndex('userIdConversation', ['_userId', 'conversationId']);
            }
        }
    });

    // Get last synced timestamp for user
    const getLastSyncedAt = async (userId) => {
        const state = await db.get('messageSyncState', userId);
        return state?.lastSyncedAt || null;
    };

    // Update last synced timestamp
    const setLastSyncedAt = async (userId, timestamp) => {
        await db.put('messageSyncState', {
            userId,
            lastSyncedAt: timestamp,
            syncInProgress: false,
            lastSyncError: null
        });
    };

    // Upsert conversations (merge backend data)
    const upsertConversations = async (userId, conversations) => {
        const tx = db.transaction('conversations', 'readwrite');
        const store = tx.objectStore('conversations');

        for (const conv of conversations) {
            const localConv = toPlain({
                ...conv,
                _userId: userId,
                _syncState: 'synced',
                _errorMessage: null
            });
            await store.put(localConv);
        }

        await tx.done;
    };

    // Upsert messages (merge backend data)
    const upsertMessages = async (userId, messages) => {
        const tx = db.transaction('messages', 'readwrite');
        const store = tx.objectStore('messages');

        for (const msg of messages) {
            // A deleted message is KEPT as a tombstone (24 Sep 2026) so the chat
            // shows "This message was deleted" in its place. toStoredMessage
            // wipes whatever this device held for it, words and photo alike.
            const localMsg = toPlain({
                ...toStoredMessage(msg),
                _userId: userId,
                _syncState: 'synced',
                _errorMessage: null,
                _optimisticId: null
            });
            await store.put(localMsg);
        }

        await tx.done;
    };

    // Get all conversations for user (sorted by lastMessageAt desc)
    const getConversations = async (userId) => {
        // Use simple userId index instead of composite index to handle null lastMessageAt
        const index = db.transaction('conversations').objectStore('conversations').index('userId');
        let conversations = await index.getAll(userId);

        // Sort descending (newest first)
        // Fall back to createdAt for conversations without messages
        conversations.sort((a, b) => {
            const aTime = a.lastMessageAt ? new Date(a.lastMessageAt) : new Date(a.createdAt);
            const bTime = b.lastMessageAt ? new Date(b.lastMessageAt) : new Date(b.createdAt);
            return bTime - aTime;
        });

        return conversations;
    };

    // Get messages for a specific conversation (sorted by createdAt asc)
    const getMessages = async (userId, conversationId) => {
        const index = db.transaction('messages').objectStore('messages').index('userIdConversationCreated');
        const range = IDBKeyRange.bound(
            [userId, conversationId, ''],
            [userId, conversationId, '\uffff']
        );
        const messages = await index.getAll(range);

        // Already sorted by createdAt in index (ISO strings sort correctly)
        return messages;
    };

    // Get single conversation
    const getConversation = async (userId, conversationId) => {
        return await db.get('conversations', [userId, conversationId]);
    };

    // Add a new message locally (optimistic)
    const addMessage = async (userId, message) => {
        const localMsg = toPlain({
            ...message,
            _userId: userId,
            _syncState: 'pending',
            _errorMessage: null
        });
        await db.put('messages', localMsg);
        return localMsg;
    };

    // Add a new conversation locally (optimistic)
    const addConversation = async (userId, conversation) => {
        const localConv = toPlain({
            ...conversation,
            _userId: userId,
            _syncState: 'pending',
            _errorMessage: null
        });
        await db.put('conversations', localConv);
        return localConv;
    };

    // Update message sync state
    const updateMessageSyncState = async (userId, messageId, state, errorMessage = null) => {
        const message = await db.get('messages', [userId, messageId]);
        if (message) {
            message._syncState = state;
            message._errorMessage = errorMessage;
            message.updatedAt = new Date().toISOString();
            await db.put('messages', message);
        }
    };

    // Update conversation sync state
    const updateConversationSyncState = async (userId, conversationId, state, errorMessage = null) => {
        const conversation = await db.get('conversations', [userId, conversationId]);
        if (conversation) {
            conversation._syncState = state;
            conversation._errorMessage = errorMessage;
            conversation.updatedAt = new Date().toISOString();
            await db.put('conversations', conversation);
        }
    };

    // Replace message with real ID after successful sync
    const replaceMessageId = async (userId, optimisticId, realId, serverMessage) => {
        const tx = db.transaction('messages', 'readwrite');
        const store = tx.objectStore('messages');

        // Delete optimistic entry
        await store.delete([userId, optimisticId]);

        // Add with real ID
        const syncedMsg = toPlain({
            ...serverMessage,
            _userId: userId,
            _syncState: 'synced',
            _errorMessage: null,
            _optimisticId: null
        });
        await store.put(syncedMsg);

        await tx.done;
        return syncedMsg;
    };

    // Replace conversation with real ID after successful sync
    const replaceConversationId = async (userId, optimisticId, realId, serverConversation) => {
        console.log('replaceConversationId called:', { userId, optimisticId, realId });
        console.log('Server conversation data:', serverConversation);

        const tx = db.transaction('conversations', 'readwrite');
        const store = tx.objectStore('conversations');

        // Check if a conversation with the real ID already exists
        const existingConv = await store.get([userId, realId]);
        console.log('Existing conversation with real ID:', existingConv);

        // Delete optimistic entry
        console.log('Deleting optimistic conversation:', [userId, optimisticId]);
        await store.delete([userId, optimisticId]);
        console.log('Optimistic conversation deleted');

        // If conversation already exists, merge server data with existing data
        // This preserves local fields like lastMessageAt, lastMessageContent, unreadCount
        const syncedConv = toPlain({
            ...(existingConv || {}),  // Preserve existing data if present
            ...serverConversation,    // Apply server updates
            _userId: userId,
            _syncState: 'synced',
            _errorMessage: null,
            _optimisticId: null,
            // Preserve these critical fields from existing conversation if server doesn't provide them
            lastMessageAt: serverConversation.lastMessageAt || existingConv?.lastMessageAt || null,
            lastMessageContent: serverConversation.lastMessageContent || existingConv?.lastMessageContent || null,
            unreadCount: existingConv?.unreadCount !== undefined ? existingConv.unreadCount : (serverConversation.unreadCount || 0)
        });

        console.log('Adding synced conversation:', syncedConv);
        await store.put(syncedConv);
        console.log('Synced conversation added');

        await tx.done;
        console.log('Transaction complete');
        return syncedConv;
    };

    // Get pending items (need to be synced)
    const getPendingMessages = async (userId) => {
        const index = db.transaction('messages').objectStore('messages').index('userIdSyncState');
        return await index.getAll([userId, 'pending']);
    };

    const getPendingConversations = async (userId) => {
        const index = db.transaction('conversations').objectStore('conversations').index('userIdSyncState');
        return await index.getAll([userId, 'pending']);
    };

    // Get failed items (errored during sync)
    const getFailedMessages = async (userId) => {
        const index = db.transaction('messages').objectStore('messages').index('userIdSyncState');
        return await index.getAll([userId, 'failed']);
    };

    const getFailedConversations = async (userId) => {
        const index = db.transaction('conversations').objectStore('conversations').index('userIdSyncState');
        return await index.getAll([userId, 'failed']);
    };

    // Delete a message from IndexedDB
    const deleteMessage = async (userId, messageId) => {
        await db.delete('messages', [userId, messageId]);
    };

    // Delete a conversation from IndexedDB
    const deleteConversation = async (userId, conversationId) => {
        await db.delete('conversations', [userId, conversationId]);
    };

    // Clear all data for a user
    const clearUserData = async (userId) => {
        const tx = db.transaction(['conversations', 'messages', 'conversationReads', 'messageSyncState'], 'readwrite');

        // Clear conversations
        const conversationIndex = tx.objectStore('conversations').index('userId');
        let conversationCursor = await conversationIndex.openCursor(userId);
        while (conversationCursor) {
            await conversationCursor.delete();
            conversationCursor = await conversationCursor.continue();
        }

        // Clear messages
        const messageIndex = tx.objectStore('messages').index('userId');
        let messageCursor = await messageIndex.openCursor(userId);
        while (messageCursor) {
            await messageCursor.delete();
            messageCursor = await messageCursor.continue();
        }

        // Clear conversation reads
        const readsIndex = tx.objectStore('conversationReads').index('userId_conversation');
        let readsCursor = await readsIndex.openCursor(IDBKeyRange.bound([userId], [userId, []]));
        while (readsCursor) {
            await readsCursor.delete();
            readsCursor = await readsCursor.continue();
        }

        // Clear sync state
        await tx.objectStore('messageSyncState').delete(userId);

        await tx.done;
    };

    // Clear all messaging data (wipe entire database)
    const clearAllData = async () => {
        const tx = db.transaction(['conversations', 'messages', 'conversationReads', 'messageSyncState', 'users'], 'readwrite');

        await tx.objectStore('conversations').clear();
        await tx.objectStore('messages').clear();
        await tx.objectStore('conversationReads').clear();
        await tx.objectStore('messageSyncState').clear();
        await tx.objectStore('users').clear();

        await tx.done;
        console.log('All messaging data cleared');
    };

    // Upsert users (cache user data from sync)
    const upsertUsers = async (users) => {
        const tx = db.transaction('users', 'readwrite');
        const store = tx.objectStore('users');

        for (const user of users) {
            const plainUser = toPlain(user);
            await store.put(plainUser);
        }

        await tx.done;
    };

    // Get user by ID from cache
    const getUser = async (userId) => {
        return await db.get('users', userId);
    };

    // Get multiple users by IDs
    const getUsers = async (userIds) => {
        const tx = db.transaction('users');
        const store = tx.objectStore('users');
        const users = [];

        for (const userId of userIds) {
            const user = await store.get(userId);
            if (user) {
                users.push(user);
            }
        }

        return users;
    };

    // Upsert conversation reads (batch insert/update read receipts from sync)
    const upsertConversationReads = async (userId, conversationReads) => {
        if (!conversationReads || conversationReads.length === 0) return;

        const tx = db.transaction('conversationReads', 'readwrite');
        const store = tx.objectStore('conversationReads');

        for (const read of conversationReads) {
            const plainRead = toPlain({
                ...read,
                _userId: userId,
                readUpToTimestamp: ensureISOTimestamp(read.readUpToTimestamp)
            });
            await store.put(plainRead);
        }

        await tx.done;
    };

    // Get all read receipts for a conversation
    const getConversationReads = async (userId, conversationId) => {
        const index = db.transaction('conversationReads').objectStore('conversationReads').index('userId_conversation');
        return await index.getAll([userId, conversationId]);
    };

    // Set/update a single conversation read receipt
    const setConversationRead = async (userId, conversationId, readUserId, timestamp) => {
        const plainRead = toPlain({
            _userId: userId,
            conversationId: conversationId,
            userId: readUserId,
            readUpToTimestamp: ensureISOTimestamp(timestamp)
        });
        await db.put('conversationReads', plainRead);
    };

    // ========================================
    // Image Upload Queue Methods
    // ========================================

    // Add image to upload queue
    const addImageToQueue = async (userId, queueItem) => {
        const plainItem = {
            ...queueItem,
            _userId: userId,
            createdAt: ensureISOTimestamp(queueItem.createdAt || new Date())
        };
        // Note: Blob objects are stored as-is in IndexedDB
        await db.put('imageUploadQueue', plainItem);
        return plainItem;
    };

    // Get all pending images in queue for user
    const getImageQueue = async (userId, status = null) => {
        if (status) {
            const index = db.transaction('imageUploadQueue').objectStore('imageUploadQueue').index('userIdStatus');
            return await index.getAll([userId, status]);
        } else {
            const index = db.transaction('imageUploadQueue').objectStore('imageUploadQueue').index('userId');
            return await index.getAll(userId);
        }
    };

    // Update queue item status
    const updateQueueItemStatus = async (userId, queueId, status, error = null, imageUploadId = null) => {
        const item = await db.get('imageUploadQueue', [userId, queueId]);
        if (item) {
            item.status = status;
            if (error) item.error = error;
            if (imageUploadId) item.imageUploadId = imageUploadId;
            item.updatedAt = new Date().toISOString();
            await db.put('imageUploadQueue', item);
        }
    };

    // Remove item from queue
    const removeFromQueue = async (userId, queueId) => {
        await db.delete('imageUploadQueue', [userId, queueId]);
    };

    // Get single queue item
    const getQueueItem = async (userId, queueId) => {
        return await db.get('imageUploadQueue', [userId, queueId]);
    };

    // Clear all queue items for a conversation
    const clearConversationQueue = async (userId, conversationId) => {
        const index = db.transaction('imageUploadQueue', 'readwrite')
            .objectStore('imageUploadQueue')
            .index('userIdConversation');

        const items = await index.getAll([userId, conversationId]);
        const tx = db.transaction('imageUploadQueue', 'readwrite');
        const store = tx.objectStore('imageUploadQueue');

        for (const item of items) {
            await store.delete([userId, item.id]);
        }

        await tx.done;
    };

    return {
        getLastSyncedAt,
        setLastSyncedAt,
        upsertConversations,
        upsertMessages,
        upsertUsers,
        upsertConversationReads,
        getConversations,
        getMessages,
        getConversation,
        getConversationReads,
        setConversationRead,
        getUser,
        getUsers,
        addMessage,
        addConversation,
        updateMessageSyncState,
        updateConversationSyncState,
        replaceMessageId,
        replaceConversationId,
        getPendingMessages,
        getPendingConversations,
        getFailedMessages,
        getFailedConversations,
        deleteMessage,
        deleteConversation,
        clearUserData,
        clearAllData,
        // Image upload queue methods
        addImageToQueue,
        getImageQueue,
        updateQueueItemStatus,
        removeFromQueue,
        getQueueItem,
        clearConversationQueue
    };
};
